import { sessionState } from '../../state';
import { escapeHtml, normalizeSafeHref } from '../../utils';
import { showToast } from '../toast';
import { copyText } from './clipboard';
import { buildCsvPreviewSrcdoc, buildMarkdownPreviewSrcdoc, PREVIEW_SRCDOC_CSP } from './preview-templates';

declare const marked: any;
declare const hljs: any;

const PREVIEW_LANGS = new Set(['html', 'svg']);
const RENDER_LANGS = new Set(['md', 'markdown', 'json', 'csv']);
const previewCodeMap = new Map<number, string>();
let previewCodeId = 0;
let markdownRenderingSetup = false;

declare global {
  interface Window {
    ccCopyCode: (btn: HTMLElement) => void;
    ccTogglePreview: (btn: HTMLElement) => void;
    ccCopyFilePath: (el: HTMLElement) => void;
  }
}

function sanitizePreviewMarkup(markup: unknown): string {
  if (!markup) return '';
  let safe = String(markup);
  safe = safe.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  safe = safe.replace(/<iframe[\s\S]*?>[\s\S]*?<\/iframe>/gi, '');
  safe = safe.replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gis, '');
  safe = safe.replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '');
  safe = safe.replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, ' $1="#"');
  safe = safe.replace(/\s(href|src)\s*=\s*javascript:[^\s>]+/gi, ' $1="#"');
  return safe;
}

function buildSafePreviewSrcdoc(markup: unknown): string {
  return `${PREVIEW_SRCDOC_CSP}${sanitizePreviewMarkup(markup)}`;
}

function buildMarkdownSrcdoc(code: string): string {
  let html;
  try { html = marked.parse(code); } catch { html = escapeHtml(code); }
  return buildMarkdownPreviewSrcdoc(html);
}

function buildCsvSrcdoc(code: string): string {
  const rows = code.trim().split('\n').map((r) => r.split(',').map((c) => c.trim().replace(/^"|"$/g, '')));
  return buildCsvPreviewSrcdoc(rows);
}

function createMarkdownRenderer() {
  const renderer = new marked.Renderer();
  renderer.html = function (html: string) {
    return escapeHtml(html || '');
  };
  renderer.link = function (href: string, title: string, text: string) {
    const rawHref = String(href || '').trim();
    if (/^\/[A-Za-z]/.test(rawHref) && !rawHref.startsWith('//')) {
      return `<a href="#" class="local-file-link" data-path="${escapeHtml(rawHref)}" onclick="ccCopyFilePath(this);return false;" title="点击复制路径">${text || escapeHtml(rawHref)}</a>`;
    }
    if (sessionState.currentCwd && /^[^/#?][^#?]*\.[a-zA-Z0-9]+$/.test(rawHref) && !/^https?:\/\//i.test(rawHref)) {
      const absPath = sessionState.currentCwd.replace(/\/$/, '') + '/' + rawHref;
      return `<a href="#" class="local-file-link" data-path="${escapeHtml(absPath)}" onclick="ccCopyFilePath(this);return false;" title="点击复制路径">${text || escapeHtml(rawHref)}</a>`;
    }
    const safeHref = normalizeSafeHref(href, { externalOnly: false, allowRelative: true });
    if (!safeHref) return text || '';
    const safeTitle = title ? ` title="${escapeHtml(title)}"` : '';
    const isExternal = /^https?:\/\//i.test(safeHref);
    const externalAttrs = isExternal ? ' target="_blank" rel="noopener noreferrer"' : '';
    return `<a href="${escapeHtml(safeHref)}"${safeTitle}${externalAttrs}>${text || ''}</a>`;
  };
  renderer.image = function (href: string, title: string, text: string) {
    const safeHref = normalizeSafeHref(href, { externalOnly: false, allowRelative: false, allowDataImage: true });
    if (!safeHref) return '';
    const safeTitle = title ? ` title="${escapeHtml(title)}"` : '';
    return `<img src="${escapeHtml(safeHref)}" alt="${escapeHtml(text || '')}"${safeTitle}>`;
  };
  renderer.code = function (code: string, language: string) {
    const lang = (language || 'plaintext').toLowerCase();
    let highlighted;
    try {
      if (hljs.getLanguage(lang)) {
        highlighted = hljs.highlight(code, { language: lang }).value;
      } else {
        highlighted = hljs.highlightAuto(code).value;
      }
    } catch {
      highlighted = escapeHtml(code);
    }
    const canPreview = PREVIEW_LANGS.has(lang);
    const canRender = RENDER_LANGS.has(lang);
    const hasAction = canPreview || canRender;
    const btnLabel = canRender ? 'View' : 'Preview';
    const renderType = canPreview ? 'html' : ((['md', 'markdown'].includes(lang)) ? 'md' : lang);
    const actionBtn = hasAction
      ? `<button class="code-preview-btn" onclick="ccTogglePreview(this)">${btnLabel}</button>`
      : '';
    const previewPane = canPreview
      ? `<div class="code-preview-pane"><iframe class="code-preview-iframe" sandbox="allow-same-origin" loading="lazy" referrerpolicy="no-referrer"></iframe></div>`
      : canRender
        ? `<div class="code-preview-pane code-render-pane" data-rtype="${escapeHtml(renderType)}"></div>`
        : '';
    const cid = hasAction ? (++previewCodeId) : 0;
    if (hasAction) previewCodeMap.set(cid, code);
    return `<div class="code-block-wrapper${hasAction ? ' has-preview' : ''}"${hasAction ? ` data-cid="${cid}"` : ''}>
    <div class="code-block-header">
      <span>${escapeHtml(lang)}</span>
      <div class="code-block-actions">${actionBtn}<button class="code-copy-btn" onclick="ccCopyCode(this)">Copy</button></div>
    </div>
    ${previewPane}<pre><code class="hljs language-${escapeHtml(lang)}">${highlighted}</code></pre>
  </div>`;
  };
  return renderer;
}

function installMarkdownGlobals(): void {
  window.ccCopyCode = function (btn: HTMLElement) {
    const wrapper = btn.closest('.code-block-wrapper') as HTMLElement | null;
    if (!wrapper) return;
    const cid = wrapper.dataset.cid ? Number(wrapper.dataset.cid) : 0;
    const codeEl = wrapper.querySelector('code');
    const code = (cid && previewCodeMap.has(cid)) ? previewCodeMap.get(cid) || '' : codeEl?.textContent || '';
    const onCopied = () => {
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = 'Copy'; }, 1500);
    };
    const onFailed = () => {
      showToast('复制失败，请手动复制代码');
    };
    copyText(code, onCopied, onFailed);
  };

  window.ccTogglePreview = function (btn: HTMLElement) {
    const wrapper = btn.closest('.code-block-wrapper') as HTMLElement | null;
    if (!wrapper) return;
    const inPreview = wrapper.classList.contains('preview-mode');
    if (inPreview) {
      wrapper.classList.remove('preview-mode');
      const renderPane = wrapper.querySelector('.code-render-pane');
      btn.textContent = renderPane ? 'View' : 'Preview';
    } else {
      const iframe = wrapper.querySelector('.code-preview-iframe') as HTMLIFrameElement | null;
      const renderPane = wrapper.querySelector('.code-render-pane') as HTMLElement | null;
      const cid = wrapper.dataset.cid ? Number(wrapper.dataset.cid) : 0;
      const rawCode = (cid && previewCodeMap.has(cid)) ? previewCodeMap.get(cid) || '' : '';
      if (iframe && !iframe.dataset.loaded) {
        iframe.srcdoc = buildSafePreviewSrcdoc(rawCode);
        iframe.dataset.loaded = '1';
      } else if (renderPane && !renderPane.dataset.loaded) {
        const rtype = renderPane.dataset.rtype || '';
        if (rtype === 'md' || rtype === 'markdown') {
          const iframe2 = document.createElement('iframe');
          iframe2.className = 'code-preview-iframe';
          iframe2.setAttribute('sandbox', 'allow-same-origin');
          iframe2.setAttribute('referrerpolicy', 'no-referrer');
          iframe2.srcdoc = buildMarkdownSrcdoc(rawCode);
          renderPane.appendChild(iframe2);
        } else if (rtype === 'csv') {
          const iframe2 = document.createElement('iframe');
          iframe2.className = 'code-preview-iframe';
          iframe2.setAttribute('sandbox', 'allow-same-origin');
          iframe2.setAttribute('referrerpolicy', 'no-referrer');
          iframe2.srcdoc = buildCsvSrcdoc(rawCode);
          renderPane.appendChild(iframe2);
        } else if (rtype === 'json') {
          const pre = document.createElement('pre');
          pre.className = 'json-render-pre';
          pre.textContent = (() => { try { return JSON.stringify(JSON.parse(rawCode), null, 2); } catch { return rawCode; } })();
          renderPane.appendChild(pre);
        }
        renderPane.dataset.loaded = '1';
      }
      wrapper.classList.add('preview-mode');
      btn.textContent = 'Source';
    }
  };

  window.ccCopyFilePath = function (el: HTMLElement) {
    const filePath = el.dataset.path || '';
    if (!filePath) return;
    copyText(filePath, () => showToast('路径已复制'), () => showToast('复制失败'));
  };
}

export function setupMarkdownRendering(): void {
  if (markdownRenderingSetup) return;
  markdownRenderingSetup = true;
  marked.setOptions({ renderer: createMarkdownRenderer(), breaks: true, gfm: true });
  installMarkdownGlobals();
}

export function clearPreviewCode(): void {
  previewCodeMap.clear();
  previewCodeId = 0;
}

export function renderMarkdown(text: string): string {
  setupMarkdownRendering();
  if (!text) return '';
  try { return marked.parse(text); }
  catch { return escapeHtml(text); }
}
